import { AddBox } from "@mui/icons-material";
import { Box, MenuItem, Stack, Typography } from "@mui/material";
import { forwardRef, JSX, Ref, useEffect, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import NoData from "../../../components/no-data/NoData";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSelect from "../../../components/side-sheet/side-sheet-select/SideSheetSelect";
import TwoStepConfirmationDialog, { bindTwoStepConfirmationDialog, useTwoStepConfirmationDialogState } from "../../../components/two-step-confirmation-dialog/TwoStepConfirmationDialog";
import VirtualizedList, { VirtualizedListItem } from "../../../components/virtualized-list/VirtualizedList";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useDeleteUICustomizationPreset } from "../../../queries/ui-customization/UseDeleteUICustomizationPreset";
import { UICustomizationPresetSortEnum, useUICustomizationPresets } from "../../../queries/ui-customization/UseUICustomizationPresets";
import i18n from "../../../services/localization-service/i18n";
import { IUICustomizationPresetDescriptionDTO } from "../../../services/ui-customization-data-service/Models";
import { useUICustomizationPresetAdminContext } from "../UICustomizationPresetAdminViewState";
import UICustomizationPresetEditorDialog from "../ui-customization-preset-editor/UICustomizationPresetEditorDialog";
import { NewPresetID } from "../ui-customization-preset-editor/UICustomizationPresetEditorEditorUtils";
import { bindUICustomizationPresetEditorDialog } from "../ui-customization-preset-editor/bindUICustomizationPresetEditorDialog";
import { useUICustomizationPresetEditorDialogState } from "../ui-customization-preset-editor/useUICustomizationPresetEditorDialogState";
import UICustomizationPresetListItem from "./UICustomizationPresetListItem";
import useUICustomizationPresetListState from "./UICustomizationPresetListState";

export type UICustomizationPreset = IUICustomizationPresetDescriptionDTO & VirtualizedListItem;

export type UICustomizationPresetListProps = {
    organizationID: number | undefined;
    visible?: boolean;
};

export type UICustomizationPresetListRefType = {
    executeAction: (action: BaseActionEnum) => void;
};

const presetSortByOptions: Array<{ value: UICustomizationPresetSortEnum, label: string; }> = [
    { value: UICustomizationPresetSortEnum.NameAsc, label: i18n.t("userRoleAdministration.sortOptionsText.nameAsc") },
    { value: UICustomizationPresetSortEnum.NameDesc, label: i18n.t("userRoleAdministration.sortOptionsText.nameDesc") },
    { value: UICustomizationPresetSortEnum.DescriptionAsc, label: i18n.t("userRoleAdministration.sortOptionsText.descriptionAsc") },
    { value: UICustomizationPresetSortEnum.DescriptionDesc, label: i18n.t("userRoleAdministration.sortOptionsText.descriptionDesc") }
];

function UICustomizationPresetList({
    organizationID,
    visible
}: UICustomizationPresetListProps, ref: Ref<UICustomizationPresetListRefType> | undefined): JSX.Element {
    const routeContext = useRouteContext();
    const {
        setPresetCount,
        setViewOptionState
    } = useUICustomizationPresetAdminContext();
    const { t } = useTranslation();
    const presetEditorDialog = useUICustomizationPresetEditorDialogState();

    const {
        confirm,
        acknowledge
    } = useConfirmation();

    const {
        state: {
            filterText,
            sortBy,
            isOptionsDirty,
            sectionExpansion
        },
        resetOptions,
        setFilterText,
        setSortBy,
        setSectionExpansion
    } = useUICustomizationPresetListState();

    useImperativeHandle<UICustomizationPresetListRefType, UICustomizationPresetListRefType>(
        ref,
        () => ({
            executeAction: onExecuteAction
        })
    );

    const {
        data: presetData,
        status: presetStatus,
        refetch: refreshPresets
    } = useQueryNotifications(useUICustomizationPresets)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        options: {
            filterText: filterText,
            sortBy: sortBy
        },
        enabled: organizationID !== undefined
    });

    const { mutateAsync: deletePreset } = useQueryNotifications(useDeleteUICustomizationPreset)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        onSuccess: (data) => {
            acknowledge(t("uiCustomizationPresetAdministration.tabs.presets.deletePresetAcknowledge", data));
        }
    });

    useEffect(() => {
        setPresetCount(presetData?.totalCount ?? 0);
    }, [
        presetData?.totalCount,
        setPresetCount
    ]);

    useEffect(() => {
        if (visible) {
            const viewOptionState = isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState(viewOptionState);
        }
    }, [
        visible,
        isOptionsDirty,
        setViewOptionState
    ]);

    function onToggleSectionExpansion(key: string) {
        setSectionExpansion(key, !sectionExpansion[key]);
    }

    function onExecuteAction(action: BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.Refresh:
                refreshPresets();
                break;

            case BaseActionEnum.CreateNew:
                createPreset();
                break;
        }
    }

    async function createPreset() {
        await presetEditorDialog.open({
            presetID: NewPresetID,
            organizationID: organizationID
        });
    }

    async function onEditPreset(preset: UICustomizationPreset) {
        await presetEditorDialog.open({
            presetID: preset.id,
            organizationID: organizationID
        });
    }

    async function onDeletePreset(preset: UICustomizationPreset) {

        const result = await confirm({
            dialogTitle: t("uiCustomizationPresetAdministration.tabs.presets.deletePresetConfirmation.title"),
            confirmationText: (preset.userCount ?? 0) > 0
                ? t("uiCustomizationPresetAdministration.tabs.presets.deletePresetConfirmation.confirmationTextWithUsers", preset)
                : t("uiCustomizationPresetAdministration.tabs.presets.deletePresetConfirmation.confirmationText"),
            showConfirmationText: true,
            yesButtonText: t("buttons.delete"),
            showYesButton: true,
            noButtonText: t("buttons.cancel"),
            showNoButton: true
        });

        if (result === "yes") {
            deletePreset(preset);
        }
    }

    function renderItem(_index: number, preset: UICustomizationPreset) {
        return (
            <UICustomizationPresetListItem
                key={`${preset.id}`}
                organizationID={organizationID}
                preset={preset}
                onEditPreset={preset => onEditPreset(preset)}
                onDeletePreset={preset => onDeletePreset(preset)}
                sectionExpansion={sectionExpansion}
                toggleSectionExpansion={onToggleSectionExpansion}
            />
        );
    }

    return (
        <>
            {visible &&
                <>
                    <LoadingZone isLoading={presetStatus === "loading"} >
                        <ContentContainer>
                            <ContentSection>
                                {presetStatus === "success" &&
                                    presetData?.totalCount === 0
                                    ? <NoData
                                        title={t("uiCustomizationPresetAdministration.tabs.presets.noData.title")}
                                        text={<Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{
                                                alignItems: "flex-end"
                                            }}>
                                            <Box>
                                                {t("uiCustomizationPresetAdministration.tabs.presets.noData.text1")}
                                            </Box>
                                            <AddBox />
                                            <Box>
                                                {t("uiCustomizationPresetAdministration.tabs.presets.noData.text2")}
                                            </Box>
                                        </Stack>
                                        }
                                    />
                                    : presetData?.filteredCount === 0
                                        ? <NoData
                                            title={t("uiCustomizationPresetAdministration.tabs.presets.noFilteredData.title")}
                                            text=""
                                        />
                                        : undefined
                                }

                                <VirtualizedList
                                    items={presetData?.data ?? []}
                                    renderItem={renderItem}
                                />
                            </ContentSection>

                            <OptionsSideSheet onReset={() => resetOptions()}>
                                <SideSheetFilterInput
                                    label={t("uiCustomizationPresetAdministration.tabs.presets.filterBy")}
                                    value={filterText}
                                    onChange={(value: string) => {
                                        setFilterText(value);
                                    }}
                                />
                                <SideSheetSelect
                                    label={t("uiCustomizationPresetAdministration.sortBy")}
                                    value={sortBy.toString()}
                                    onChange={(value: string) => {
                                        setSortBy(Number(value));
                                    }}
                                >
                                    {presetSortByOptions.map((o, index) => {
                                        return (
                                            <MenuItem key={index}
                                                value={o.value}
                                            >
                                                {o.label}
                                            </MenuItem>
                                        );
                                    })}
                                </SideSheetSelect>
                            </OptionsSideSheet>
                        </ContentContainer>
                    </LoadingZone>

                    <UICustomizationPresetEditorDialog {...bindUICustomizationPresetEditorDialog(presetEditorDialog)} />
                </>
            }
        </>
    );
}

export default forwardRef(UICustomizationPresetList);